import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IntlProvider, addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';
import fi from 'react-intl/locale-data/fi';
import sv from 'react-intl/locale-data/sv';
import styled from 'styled-components';

import {
  changeLocale,
  exportLicenses,
  fetchLicenses,
  fetchLicenseLogs,
  fetchLicenseTypes,
  fetchSellers,
  importLicenses,
  logout,
  onLogin,
  saveLicensePayment,
} from '~actions'; // eslint-disable-line

import {
  getLicenseSellers,
  getLicenseTypes,
} from '~selectors'; // eslint-disable-line

import LicenseTable from './LicenseTable';
import LoadingIndicator from '../common/LoadingIndicator';
import Navigation from '../common/Navigation';

import fiFI from '../../localisations/fi_FI.json';
import svSE from '../../localisations/sv_SE.json';
import enGB from '../../localisations/en_GB.json';

import { License, Log } from './schema';

addLocaleData([...en, ...fi, ...sv]);
const messages = {
  fi: fiFI,
  sv: svSE,
  en: enGB,
};

const LoadingScreen = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const Overlay = styled.div`
  background: hsla(0, 0%, 100%, 0.75);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const TableWrap = styled.div`
  position: relative;
`;

const propTypes = {
  changeLocale: PropTypes.func.isRequired,
  exportLicenses: PropTypes.func.isRequired,
  fetchLicenses: PropTypes.func.isRequired,
  fetchLicenseLogs: PropTypes.func.isRequired,
  fetchLicenseTypes: PropTypes.func.isRequired,
  fetchSellers: PropTypes.func.isRequired,
  licenseFilters: PropTypes.object.isRequired,
  licenseLogFilters: PropTypes.object.isRequired,
  history: PropTypes.object,
  licenses: PropTypes.array.isRequired,
  licenseLogs: PropTypes.array.isRequired,
  // licensesFetched: PropTypes.number,
  locale: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  licensePagination: PropTypes.object.isRequired,
  licenseLogPagination: PropTypes.object.isRequired,
  licenseTypes: PropTypes.array.isRequired,
  session: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  sellers: PropTypes.array.isRequired,
};

class LicenseManagement extends Component {
  static isAuthorized = (routerProps, session) => {
    const ALLOWED = ['license_retailer', 'license_admin', 'super_user'];
    if (session.invalidLogin) {
      return false;
    }
    const { logins } = session;
    if (!logins.tilaajavastuu) {
      return false;
    }
    const { tilaajavastuu: { client } } = logins;
    if (!client) {
      return false;
    }
    const matchinLevels = client.access_levels.filter(
      ual => ALLOWED.indexOf(ual) !== -1
    );
    if (matchinLevels.length) {
      return true;
    }
    return false;
  }


  state = {
    tableView: 'licenses',
  }

  componentWillMount = () => {
    this.props.fetchLicenseTypes();
    this.props.fetchSellers({ appendOrgInfo: true });
  }

  onExport = ({ andFilters, orFilters, pagination, searchTerms }) => {
    this.props.exportLicenses({
      andFilters,
      orFilters,
      pagination,
      searchTerms,
      type: this.state.tableView,
    });
  }

  onFetch = ({ andFilters, orFilters, pagination, searchTerms }) => {
    if (this.state.tableView === this.TABLE_VIEW_LICENSES) {
      this.props.fetchLicenses({
        andFilters,
        orFilters,
        pagination,
        searchTerms,
      });
    } else if (this.state.tableView === this.TABLE_VIEW_BILLING) {
      this.props.fetchLicenseLogs({
        andFilters,
        orFilters,
        pagination,
        searchTerms,
      });
    }
  }

  onChangeTableView = tableView => {
    this.setState({ tableView }, () => {
      this.onFetch({
        filters: this.tableFilters,
        pagination: this.tablePagination,
      });
    });
  }

  get userIsAdmin() {
    const ADMIN_LEVELS = ['license_admin', 'super_user'];
    const { session } = this.props;
    if (session.invalidLogin) {
      return false;
    }
    const { logins } = session;
    if (!logins.tilaajavastuu) {
      return false;
    }
    const { tilaajavastuu: { client } } = logins;
    if (!client) {
      return false;
    }
    const matchinLevels = client.access_levels.filter(
      ual => ADMIN_LEVELS.indexOf(ual) !== -1
    );
    if (matchinLevels.length) {
      // If user is admin, there's no need to check anything regarding the
      // currently active license.
      return true;
    }
    return false;
  }

  get tableCols() {
    return this.state.tableView === this.TABLE_VIEW_LICENSES
      ? this.VISIBLE_TABLE_FIELDS_LICENSE
      : this.VISIBLE_TABLE_FIELDS_BILLING;
  }

  get tableFilters() {
    return this.state.tableView === this.TABLE_VIEW_LICENSES
      ? this.props.licenseFilters
      : this.props.licenseLogFilters;
  }

  get tableRows() {
    return this.state.tableView === this.TABLE_VIEW_LICENSES
      ? this.props.licenses
      : this.props.licenseLogs;
  }

  get tablePagination() {
    return this.state.tableView === this.TABLE_VIEW_LICENSES
      ? this.props.licensePagination
      : this.props.licenseLogPagination;
  }

  get tableSchema() {
    return this.state.tableView === this.TABLE_VIEW_LICENSES
      ? License.schema
      : Log.schema;
  }

  TABLE_VIEW_LICENSES = 'licenses';
  TABLE_VIEW_BILLING = 'billing';

  VISIBLE_TABLE_FIELDS_LICENSE = [
    'company.name',
    'company.businessId',
    'company.website',
    'contactPerson.firstName',
    'contactPerson.lastName',
    'contactPerson.phone',
    'contactPerson.email',
    'licenseType.name',
    'license.status',
    'license.startDate',
    'license.endDate',
    'licenseType.rrpNoVat',
    'licenseType.periodInMonths',
    'seller.email',
    'retailer.name',
  ];
  VISIBLE_TABLE_FIELDS_BILLING = [
    'company.name',
    'company.businessId',
    'licenseLog.status',
    'licenseLog.startDate',
    'licenseLog.endDate',
    'licenseType.name',
    'licenseType.periodInMonths',
    'licenseLog.priceNoVat',
    'licenseLog.billedAt',
    'licenseLog.paidAt',
  ];

  render = () => {
    const navProps = {
      changeLocale: this.props.changeLocale,
      history: this.props.history,
      locale: this.props.locale,
      login: this.props.onLogin,
      logout: this.props.logout,
      session: this.props.session,
    };

    const retailersMap = this.props.sellers
      .map(seller => ({
        'id': seller.orgId,
        'name': seller.orgName,
      }))
      .reduce((retailersIdMap, retailer) => {
        retailersIdMap[retailer.id] = retailer;
        return retailersIdMap;
      }, {});
    return (
      <div className='LicenseManagement'>
        <IntlProvider
          locale={this.props.locale.lang}
          messages={messages[this.props.locale.lang]}
        >
          <React.Fragment>
            <Navigation {...navProps} />
            <h1>Lisenssihallinta</h1>
            <TableWrap>
              <LicenseTable
                cols={this.tableCols}
                onExport={this.onExport}
                onFetch={this.onFetch}
                filters={this.tableFilters}
                onChangeTableView={this.onChangeTableView}
                pagination={this.tablePagination}
                rows={this.tableRows}
                schema={this.tableSchema}
                view={this.state.tableView}
                loading={false}
                licenseTypes={this.props.licenseTypes}
                retailers={Object.values(retailersMap)}
                retailerPersons={this.props.sellers}
              />
              {this.props.loading &&
                <Overlay>
                  <LoadingScreen>
                    <LoadingIndicator />
                  </LoadingScreen>
                </Overlay>
              }
            </TableWrap>
          </React.Fragment>
        </IntlProvider>
      </div>
    );
  }
}

LicenseManagement.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    licenseFilters: state.licenses.licenses.filters,
    licenseLogFilters: state.licenses.logs.filters,
    licenses: state.licenses.licenses.items,
    licenseLogs: state.licenses.logs.items,
    licensesFetched: state.licenses.licenses.fetched,
    licenseTypes: getLicenseTypes(state),
    migration: state.licenses.migration,
    licensePagination: state.licenses.licenses.pagination,
    licenseLogPagination: state.licenses.logs.pagination,
    payment: state.licenses.payment,
    locale: state.locale,
    session: state.session,
    loading: state.licenses.actionInProgress,
    sellers: getLicenseSellers(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      changeLocale,
      exportLicenses,
      fetchLicenses,
      fetchLicenseLogs,
      fetchLicenseTypes,
      fetchSellers,
      importLicenses,
      logout,
      onLogin,
      saveLicensePayment,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(LicenseManagement);
